import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import type { AutoLabelDefinition } from '@/types/email';

export interface AutoLabelStoreState {
  labels: Record<string, AutoLabelDefinition>;
  labelOrder: string[];
  initialized: boolean;
  setLabels: (labels: AutoLabelDefinition[]) => void;
  upsertLabel: (label: AutoLabelDefinition) => void;
  removeLabel: (labelId: string) => void;
}

const sortLabelOrder = (labels: Record<string, AutoLabelDefinition>, order: string[]): string[] => {
  return [...order]
    .filter((id) => labels[id])
    .sort((a, b) => labels[a].name.localeCompare(labels[b].name));
};

export const useAutoLabelStore = create<AutoLabelStoreState>()(
  conditionalDevtools(
    (set) => ({
      labels: {},
      labelOrder: [],
      initialized: false,
      setLabels: (labelList) => {
        const map: Record<string, AutoLabelDefinition> = {};
        labelList.forEach((label) => {
          map[label.id] = label;
        });
        const order = labelList
          .map((label) => label.id)
          .sort((a, b) => map[a].name.localeCompare(map[b].name));
        set({ labels: map, labelOrder: order, initialized: true });
      },
      upsertLabel: (label) =>
        set((state) => {
          const labels = { ...state.labels, [label.id]: label };
          const orderSet = new Set([...state.labelOrder, label.id]);
          const labelOrder = sortLabelOrder(labels, Array.from(orderSet));
          return { labels, labelOrder };
        }),
      removeLabel: (labelId) =>
        set((state) => {
          if (!state.labels[labelId]) {
            return state;
          }
          const labels = { ...state.labels };
          delete labels[labelId];
          return {
            labels,
            labelOrder: state.labelOrder.filter((id) => id !== labelId),
          };
        }),
    }),
    { name: 'auto-label-store' }
  )
);
